import Box from "@mui/material/Box";
import type { KeyboardEvent } from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, LabelList, Legend, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import ChartLegend from "./ChartLegend";
import { SERIES_COLORS } from "./palette";
import { withPartial } from "./granularity/granularity";

/** Opacidade das barras de bucket parcial (cortado pelo período ou ainda em andamento). */
export const PARTIAL_OPACITY = 0.45;

export interface ComboDatum { x: string; label: string; [serie: string]: number | string | null }
export interface ComboSeries { key: string; label: string; color?: string }
export interface ComboMarker { x: string; number: number; label: string; kind: "system" | "action"; selected?: boolean }
export interface ComboWindow { from: string; to: string; label: string; tone: "before" | "after" }
export interface ComboChartProps {
  data: ComboDatum[];
  bars: ComboSeries[];
  stacked?: boolean;
  /** Linha no eixo direito (ex.: penetração em fração). */
  /** `axis` padrão "right" (eixo próprio); "left" divide o eixo das barras. `dashed` = traço "5 4". */
  line?: ComboSeries & { format?: (v: number) => string; axis?: "left" | "right"; dashed?: boolean };
  markers?: ComboMarker[];
  windows?: ComboWindow[];
  /** `barKey` = série da barra clicada (ex.: canal na barra empilhada), para o drill herdar o recorte. */
  onBarClick?: (x: string, barKey?: string) => void;
  onMarkerClick?: (marker: ComboMarker) => void;
  valueFormatter?: (v: number) => string;
  /** Formato só dos ticks do eixo esquerdo (padrão: `valueFormatter`). Use rótulos curtos e sem quebra (ex.: `formatAxisBRL`). */
  yTickFormatter?: (v: number) => string;
  /** Domínio fixo do eixo esquerdo (ex.: [0, 100] no gráfico 100% empilhado: o arredondamento não estica até 120%). */
  yDomain?: [number, number];
  /** Ticks explícitos do eixo esquerdo. */
  yTicks?: number[];
  /** Largura do eixo esquerdo em px (padrão do recharts: 60 — pouco para "R$ 150 mil"). */
  yAxisWidth?: number;
  /** Rótulo de valor sobre cada barra: `true` usa o `valueFormatter`; função = formato próprio. Padrão: sem rótulo. */
  barLabels?: boolean | ((v: number) => string);
  /** "top" (padrão) = acima da barra; "inside" = dentro da base da barra, em branco — longe do marcador da linha (V26);
   *  "auto" = dentro quando a barra comporta o texto, ACIMA quando é baixa (o rótulo de barra baixa ficava cortado/coberto). */
  barLabelPosition?: "top" | "inside" | "auto";
  /** Legenda em HTML abaixo do gráfico (V32): reserva o espaço desde o 1º render, sem cobrir o eixo X. */
  htmlLegend?: boolean;
  height?: number;
  width?: number;
  ariaLabel: string;
  /** Buckets (`x`) parciais: barra mais clara e "(parcial)" no tooltip. */
  partialXs?: string[];
}

// Mockup G1: vermelho = marco do sistema (taxa), roxo = marco de ação registrado.
export const MARKER_COLORS = { system: "#d64545", action: "#7c5cd6" } as const;
export const LINE_COLOR = "#e8710a";
const WINDOW_FILL = { before: "#e5e7eb", after: "#cff4fc" } as const;
const PIN_R = 9;
const PIN_STEP = PIN_R * 2 + 2;
const BASE_TOP = 28;

type PinLabelProps = { viewBox?: { x: number; y: number }; marker: ComboMarker; onClick?: (m: ComboMarker) => void; slot?: number; of?: number };

/** Vários marcos no mesmo bucket (ex.: 4 marcos em out/26 no mockup): espalha na horizontal e alterna a altura. */
export function pinOffset(slot: number, of: number) {
  return { dx: (slot - (of - 1) / 2) * PIN_STEP, dy: slot % 2 === 1 ? PIN_R + 4 : 0 };
}

// Pino numerado no topo da linha de referência; com onClick é um botão acessível (Enter/Espaço também disparam).
function PinLabel({ viewBox, marker, onClick, slot = 0, of = 1 }: PinLabelProps) {
  const { dx, dy } = pinOffset(slot, of);
  const cx = (viewBox?.x ?? 0) + dx;
  const cy = (viewBox?.y ?? 0) - PIN_R - 2 - dy;
  const fire = () => onClick?.(marker);
  const onKeyDown = (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fire();
    }
  };
  return (
    <g {...(onClick ? { role: "button", tabIndex: 0, "aria-pressed": !!marker.selected, onClick: fire, onKeyDown } : { role: "img" })}
      aria-label={`Marco ${marker.number}: ${marker.label}`} style={{ cursor: onClick ? "pointer" : "default" }}>
      <title>{`${marker.number}. ${marker.label}`}</title>
      <circle cx={cx} cy={cy} r={PIN_R} fill={MARKER_COLORS[marker.kind]} stroke={marker.selected ? "#17384c" : "#fff"} strokeWidth={marker.selected ? 2.5 : 1.5} />
      <text x={cx} y={cy} dy="0.35em" textAnchor="middle" fill="#fff" fontSize={11} fontWeight={600}>{marker.number}</text>
    </g>
  );
}

const fmt = (v: number, f?: (v: number) => string) => (f ? f(v) : v);

/** Altura mínima (px) para o rótulo caber dentro da barra; abaixo disso ele vai para cima dela. */
export const LOW_BAR_PX = 22;
const LABEL_DARK = "#17384c";

type AutoLabelProps = { x?: number | string; y?: number | string; width?: number | string; height?: number | string; value?: number | string };
/** Rótulo "auto": dentro da base (branco) em barra alta; acima (escuro) em barra baixa. */
export function autoBarLabel(format?: (v: number) => string) {
  return function AutoBarLabel({ x = 0, y = 0, width = 0, height = 0, value }: AutoLabelProps) {
    if (value === undefined || value === null) return null;
    const [nx, ny, nw, nh] = [x, y, width, height].map(Number);
    const text = format ? format(Number(value)) : String(value);
    const low = Math.abs(nh) < LOW_BAR_PX;
    return (
      <text x={nx + nw / 2} y={low ? ny - 4 : ny + nh - 6} textAnchor="middle" fontSize={11}
        fill={low ? LABEL_DARK : "#fff"} fontWeight={700} data-position={low ? "above" : "inside"}>
        <tspan>{text}</tspan>
      </text>
    );
  };
}

/** Valor do tooltip: a série da linha usa o formato do eixo direito; as barras, o `valueFormatter`. */
export const comboTooltipValue = (v: number, name: string, line?: ComboChartProps["line"], valueFormatter?: (v: number) => string) =>
  line && name === line.label ? fmt(v, line.format ?? (line.axis === "left" ? valueFormatter : undefined)) : fmt(v, valueFormatter);

// width fixo = sem ResponsiveContainer (que mede 0 no jsdom); sem width = ocupa 100% do pai.
export default function ComboChart({ data, bars, stacked, line, markers = [], windows = [], onBarClick, onMarkerClick, valueFormatter, yTickFormatter, yDomain, yTicks, yAxisWidth, barLabels, barLabelPosition = "top", htmlLegend, height = 280, width, ariaLabel, partialXs = [] }: ComboChartProps) {
  const partial = new Set(partialXs);
  const barLabelFormat = typeof barLabels === "function" ? barLabels : valueFormatter;
  const labelOf = (x: string) => data.find((d) => d.x === x)?.label ?? x;
  const perX = new Map<string, number>();
  const slots = markers.map((m) => {
    const slot = perX.get(m.x) ?? 0;
    perX.set(m.x, slot + 1);
    return slot;
  });
  const pinsStacked = markers.some((_, i) => slots[i] > 0);
  const chart = (
    <ComposedChart data={data} width={width} height={height} margin={{ top: pinsStacked ? BASE_TOP + PIN_R + 4 : BASE_TOP, right: 24, bottom: 8, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      {/* Margem direita 24 + preserveStartEnd: o último rótulo (ex.: "set/26") não some por não caber. */}
      <XAxis dataKey="x" tickFormatter={labelOf} interval="preserveStartEnd" />
      <YAxis yAxisId="left" tickFormatter={yTickFormatter ?? valueFormatter} {...(yDomain ? { domain: yDomain, allowDataOverflow: true } : {})}
        {...(yTicks ? { ticks: yTicks } : {})} {...(yAxisWidth ? { width: yAxisWidth } : {})} />
      {line && line.axis !== "left" && <YAxis yAxisId="right" orientation="right" tickFormatter={line.format} />}
      <Tooltip labelFormatter={(x: string) => withPartial(labelOf(x), partial.has(x))}
        formatter={(v: number, name: string) => comboTooltipValue(v, name, line, valueFormatter)} />
      {!htmlLegend && <Legend />}
      {windows.map((w) => (
        <ReferenceArea key={w.tone} yAxisId="left" x1={w.from} x2={w.to} fill={WINDOW_FILL[w.tone]} fillOpacity={0.6}
          label={{ value: w.label, position: "insideTop", fontSize: 11 }} />
      ))}
      {bars.map((b, i) => (
        <Bar key={b.key} yAxisId="left" dataKey={b.key} name={b.label} stackId={stacked ? "s" : undefined} isAnimationActive={false}
          fill={b.color ?? SERIES_COLORS[i % SERIES_COLORS.length]} cursor={onBarClick ? "pointer" : undefined}
          onClick={(d: { x?: string; payload?: { x: string } }) => onBarClick?.(d.payload?.x ?? (d.x as string), b.key)}>
          {partial.size > 0 && data.map((d) => <Cell key={d.x} fillOpacity={partial.has(d.x) ? PARTIAL_OPACITY : 1} />)}
          {barLabels && barLabelPosition === "auto" && (
            <LabelList dataKey={b.key} content={autoBarLabel(barLabelFormat)} />
          )}
          {barLabels && barLabelPosition !== "auto" && (
            <LabelList dataKey={b.key} position={barLabelPosition === "inside" ? "insideBottom" : "top"} fontSize={11}
              {...(barLabelPosition === "inside" ? { fill: "#fff", fontWeight: 700 } : {})}
              formatter={(v: number) => (barLabelFormat ? barLabelFormat(v) : v)} />
          )}
        </Bar>
      ))}
      {line && <Line yAxisId={line.axis === "left" ? "left" : "right"} dataKey={line.key} name={line.label} stroke={line.color ?? LINE_COLOR} strokeWidth={2} strokeDasharray={line.dashed ? "5 4" : undefined} dot isAnimationActive={false} />}
      {markers.map((m, i) => (
        <ReferenceLine key={`${m.kind}-${m.number}`} yAxisId="left" x={m.x} ifOverflow="visible" stroke={MARKER_COLORS[m.kind]}
          strokeWidth={m.selected ? 2 : 1.5} strokeDasharray={m.selected ? undefined : "4 3"}
          label={<PinLabel marker={m} onClick={onMarkerClick} slot={slots[i]} of={perX.get(m.x) ?? 1} />} />
      ))}
    </ComposedChart>
  );
  // role="figure" (não "img"): filhos de img são apresentacionais e esconderiam os pinos-botão do leitor de tela.
  return (
    <Box role="figure" aria-label={ariaLabel} sx={{ width: width ? `${width}px` : "100%" }}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
      {htmlLegend && (
        <ChartLegend items={[
          ...bars.map((b, i) => ({ label: b.label, color: b.color ?? SERIES_COLORS[i % SERIES_COLORS.length], kind: "bar" as const })),
          ...(line ? [{ label: line.label, color: line.color ?? LINE_COLOR, dashed: line.dashed }] : []),
        ]} note={partial.size > 0 ? "barra clara = parcial" : undefined} />
      )}
    </Box>
  );
}
